import { NumberRange, SegmentedControl } from "@blueprintjs/core";
import {
    ProjectFilterTableName,
    segmentTrackChangeProjectFilter
} from "in_queue/analytics";
import { getStudyGroupDisplayLabel } from "in_queue/types/clusterProjectRowType";
import { formatCosts } from "in_queue/utils/formatting";
import { capitalize, countBy, identity, sortBy, uniq } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { formatFuelType } from "types/queueProjectRelatednessType";
import { RegionType } from "types/regionType";
import { BaseScenarioStats } from "../agGrid/baseScenarioColumns";
import { FilterSearchInput } from "./FilterSearchInput";
import { QuickFilterOption, QuickFilterSelect } from "./QuickFilterSelect";
import {
    isFullRange,
    RangeFilterField,
    RangeFilterSelect
} from "./RangeFilterSelect";
import {
    ToggleFilterField,
    ToggleFilterSelect,
    TOGGLE_ALL
} from "./ToggleFilterSelect";

const TRACKING_ALL = TOGGLE_ALL;
const TRACKING_TRACKED = "tracked";
const TRACKING_UNTRACKED = "untracked";

export interface ProjectFilterCriteria {
    readonly fuelType: string;
    readonly studyGroup: string;
    readonly isTracked: boolean;
    readonly stats: BaseScenarioStats | undefined;
}

export type ProjectFilterToggle<TRow> = Omit<
    ToggleFilterField,
    "value" | "onChange"
> & {
    readonly getValue: (row: TRow) => string | undefined;
};

const NO_TOGGLES: ReadonlyArray<ProjectFilterToggle<unknown>> = [];

const matchesFilter = (selected: ReadonlySet<string>, value: string): boolean =>
    selected.size === 0 || selected.has(value);

type MultiSelectFilterType = "studyGroup" | "fuelType";

const multiSelectFilterOptions = (
    values: ReadonlyArray<string>,
    countedValues: ReadonlyArray<string>,
    labelFor: (value: string) => string
): ReadonlyArray<QuickFilterOption> => {
    const counts = countBy(countedValues);
    return sortBy(
        uniq(values).map((value) => ({
            value,
            label: labelFor(value),
            count: counts[value] ?? 0
        })),
        (option) => option.label
    );
};

// The percentile the base scenario assigns, which is already a 1-100 figure.
const PERCENTILE_RANGE: NumberRange = [1, 100];
const AXIS_STOPS = 4;

// Evenly spaced ticks across the span, always ending on `max`.
const axisLabels = (min: number, max: number): number[] => {
    if (max <= min) {
        return [min];
    }
    const values = Array.from({ length: AXIS_STOPS }, (_, index) =>
        Math.round(min + ((max - min) * index) / (AXIS_STOPS - 1))
    );
    return Array.from(new Set(values));
};

// A field left at its full span passes everything, including rows the base
// scenario never scored. Narrowing it drops those rows, since they have no
// value to compare.
const matchesRange = (
    field: RangeFilterField,
    value: number | undefined
): boolean => {
    if (isFullRange(field)) {
        return true;
    }
    if (value === undefined) {
        return false;
    }
    return value >= field.value[0] && value <= field.value[1];
};

const clampRange = (range: NumberRange | null, max: number): NumberRange =>
    range === null
        ? [0, max]
        : [Math.min(range[0], max), Math.min(range[1], max)];

const formatRange = ([min, max]: NumberRange): string => `${min}-${max}`;
const formatSet = (values: ReadonlySet<string>): string =>
    Array.from(values).sort().join(",");

export const useProjectFilters = <TRow,>({
    tableName,
    rows,
    filterCriteria,
    region,
    showStudyGroup,
    labels,
    toggles
}: {
    readonly tableName: ProjectFilterTableName;
    readonly rows: ReadonlyArray<TRow>;
    readonly filterCriteria: (row: TRow) => ProjectFilterCriteria;
    readonly region: RegionType;
    readonly showStudyGroup: boolean;
    readonly labels: { readonly studyGroup: string; readonly tracked: string };
    readonly toggles?: {
        readonly label: string;
        readonly untrackedLabel: string;
        readonly fields: ReadonlyArray<ProjectFilterToggle<TRow>>;
    };
}) => {
    const [filterText, setFilterText] = useState("");
    const [studyGroups, setStudyGroups] = useState<ReadonlySet<string>>(
        new Set()
    );
    const [fuelTypes, setFuelTypes] = useState<ReadonlySet<string>>(new Set());
    const [tracking, setTracking] = useState(TRACKING_ALL);
    const [percentiles, setPercentiles] =
        useState<NumberRange>(PERCENTILE_RANGE);
    const [upgrades, setUpgrades] = useState<NumberRange | null>(null);
    const [costPerMw, setCostPerMw] = useState<NumberRange | null>(null);
    const [toggleValues, setToggleValues] = useState<
        Readonly<Record<string, string>>
    >({});
    const toggleFields: ReadonlyArray<ProjectFilterToggle<TRow>> =
        toggles?.fields ?? NO_TOGGLES;

    const tracked = useCallback(
        <T,>(
                set: (value: T) => void,
                filterName: string,
                format: (value: T) => string
            ) =>
            (value: T) => {
                set(value);
                segmentTrackChangeProjectFilter({
                    tableName,
                    filterName,
                    value: format(value)
                });
            },
        [tableName]
    );

    const allCriteria = useMemo(
        () => rows.map(filterCriteria),
        [rows, filterCriteria]
    );
    const matchesDropdowns = useCallback(
        (
            criteria: ProjectFilterCriteria,
            row: TRow,
            ignore?: MultiSelectFilterType
        ): boolean =>
            (ignore === "studyGroup" ||
                matchesFilter(studyGroups, criteria.studyGroup)) &&
            (ignore === "fuelType" ||
                matchesFilter(fuelTypes, criteria.fuelType)) &&
            (tracking === TRACKING_ALL ||
                criteria.isTracked === (tracking === TRACKING_TRACKED)) &&
            toggleFields.every((toggle) => {
                const value = toggleValues[toggle.key] ?? TOGGLE_ALL;
                const rowValue = toggle.getValue(row);
                return (
                    value === TOGGLE_ALL ||
                    rowValue === undefined ||
                    rowValue === value
                );
            }),
        [studyGroups, fuelTypes, tracking, toggleFields, toggleValues]
    );

    // The range axes span the rows that pass the dropdowns, so narrowing a
    // dropdown rescales them.
    const { maxUpgrades, maxCostPerMw } = useMemo(() => {
        const inDropdowns = allCriteria.filter((criteria, index) =>
            matchesDropdowns(criteria, rows[index])
        );
        return {
            maxUpgrades: Math.max(
                1,
                ...inDropdowns.map(
                    (criteria) => criteria.stats?.numAssignedUpgrades ?? 0
                )
            ),
            // Whole dollars, so a typed bound that rounds to the max still reads as the full span.
            maxCostPerMw: Math.ceil(
                Math.max(
                    1,
                    ...inDropdowns.map(
                        (criteria) => criteria.stats?.costPerMw ?? 0
                    )
                )
            )
        };
    }, [allCriteria, rows, matchesDropdowns]);

    const baseResultFields = useMemo(
        (): ReadonlyArray<RangeFilterField> => [
            {
                key: "percentile",
                label: "Rank percentile",
                min: PERCENTILE_RANGE[0],
                max: PERCENTILE_RANGE[1],
                labelValues: axisLabels(
                    PERCENTILE_RANGE[0],
                    PERCENTILE_RANGE[1]
                ),
                value: percentiles,
                onChange: tracked(setPercentiles, "percentile", formatRange)
            },
            {
                key: "upgrades",
                label: "# of assigned upgrades",
                min: 0,
                max: maxUpgrades,
                labelValues: axisLabels(0, maxUpgrades),
                value: clampRange(upgrades, maxUpgrades),
                onChange: tracked(setUpgrades, "upgrades", formatRange)
            },
            {
                key: "costPerMw",
                label: "$/MW",
                min: 0,
                max: maxCostPerMw,
                labelValues: axisLabels(0, maxCostPerMw),
                labelRenderer: formatCosts,
                value: clampRange(costPerMw, maxCostPerMw),
                onChange: tracked(setCostPerMw, "cost_per_mw", formatRange)
            }
        ],
        [percentiles, upgrades, maxUpgrades, costPerMw, maxCostPerMw, tracked]
    );

    const matchesRanges = useCallback(
        (criteria: ProjectFilterCriteria): boolean =>
            matchesRange(
                baseResultFields[0],
                criteria.stats?.rankStats?.percentile
            ) &&
            matchesRange(
                baseResultFields[1],
                criteria.stats?.numAssignedUpgrades
            ) &&
            matchesRange(baseResultFields[2], criteria.stats?.costPerMw),
        [baseResultFields]
    );
    // Each dropdown count is scoped to the number of rows that pass the other filters.
    // For e.g if a subregion is selected,
    // the fuel type counts will only include rows that are in that subregion.
    const optionsFor = useCallback(
        (
            multiSelectFilterType: MultiSelectFilterType,
            labelFor: (value: string) => string
        ) =>
            multiSelectFilterOptions(
                allCriteria.map((criteria) => criteria[multiSelectFilterType]),
                allCriteria
                    .filter(
                        (criteria, index) =>
                            matchesDropdowns(
                                criteria,
                                rows[index],
                                multiSelectFilterType
                            ) && matchesRanges(criteria)
                    )
                    .map((criteria) => criteria[multiSelectFilterType]),
                labelFor
            ),
        [allCriteria, rows, matchesDropdowns, matchesRanges]
    );
    const studyGroupOptions = useMemo(
        () =>
            optionsFor("studyGroup", (value) =>
                getStudyGroupDisplayLabel(region, value)
            ),
        [optionsFor, region]
    );
    const fuelTypeOptions = useMemo(
        () =>
            optionsFor("fuelType", (value) =>
                capitalize(formatFuelType(value))
            ),
        [optionsFor]
    );

    const rowFilter = useCallback(
        (row: TRow): boolean => {
            const criteria = filterCriteria(row);
            return matchesDropdowns(criteria, row) && matchesRanges(criteria);
        },
        [filterCriteria, matchesDropdowns, matchesRanges]
    );

    const hasActiveFilters =
        filterText !== "" ||
        studyGroups.size > 0 ||
        fuelTypes.size > 0 ||
        tracking !== TRACKING_ALL ||
        Object.values(toggleValues).some((value) => value !== TOGGLE_ALL) ||
        baseResultFields.some((field) => !isFullRange(field));

    const reset = useCallback(() => {
        setFilterText("");
        setStudyGroups(new Set());
        setFuelTypes(new Set());
        setTracking(TRACKING_ALL);
        setPercentiles(PERCENTILE_RANGE);
        setUpgrades(null);
        setCostPerMw(null);
        setToggleValues({});
    }, []);

    const onTrackingChange = tracked(setTracking, "tracking", identity);
    const onStudyGroupsChange = tracked(
        setStudyGroups,
        "study_group",
        formatSet
    );
    const onFuelTypesChange = tracked(setFuelTypes, "fuel_type", formatSet);

    const actions = [
        <FilterSearchInput
            key="search"
            value={filterText}
            onChange={setFilterText}
        />,
        ...(toggles
            ? [
                  <ToggleFilterSelect
                      key="toggles"
                      label={toggles.label}
                      fields={[
                          {
                              key: "tracking",
                              label: labels.tracked,
                              options: [
                                  {
                                      label: labels.tracked,
                                      value: TRACKING_TRACKED
                                  },
                                  {
                                      label: toggles.untrackedLabel,
                                      value: TRACKING_UNTRACKED
                                  }
                              ],
                              value: tracking,
                              onChange: onTrackingChange
                          },
                          ...toggleFields.map((toggle) => ({
                              ...toggle,
                              value: toggleValues[toggle.key] ?? TOGGLE_ALL,
                              onChange: tracked(
                                  (value: string) =>
                                      setToggleValues((previous) => ({
                                          ...previous,
                                          [toggle.key]: value
                                      })),
                                  toggle.key,
                                  identity
                              )
                          }))
                      ]}
                  />
              ]
            : []),
        ...(showStudyGroup
            ? [
                  <QuickFilterSelect
                      key="group"
                      label={labels.studyGroup}
                      options={studyGroupOptions}
                      selected={studyGroups}
                      onChange={onStudyGroupsChange}
                  />
              ]
            : []),
        <QuickFilterSelect
            key="fuelType"
            label="Fuel Type"
            options={fuelTypeOptions}
            selected={fuelTypes}
            onChange={onFuelTypesChange}
        />,
        <RangeFilterSelect
            key="baseResults"
            label="Base Results"
            fields={baseResultFields}
        />,
        ...(toggles
            ? []
            : [
                  <SegmentedControl
                      key="tracking"
                      small
                      value={tracking}
                      onValueChange={onTrackingChange}
                      options={[
                          { label: "All", value: TRACKING_ALL },
                          { label: labels.tracked, value: TRACKING_TRACKED }
                      ]}
                  />
              ])
    ];

    return { actions, filterText, rowFilter, reset, hasActiveFilters };
};
